/**
 * The add / edit form for one feature found in the ditch — the office dig page
 * and the field dig sheet both use it (the field reads snake_case, so callers
 * hand in plain initial strings).
 *
 * UT minimum WT, pit depth, length and width are in the tenant's WT unit. The
 * remaining wall is NOT entered or computed here — the API works it out from
 * the nominal WT and shows it back.
 */
import React, { useState } from 'react';
import type { PfCatalog } from '../../../types/pipeline-facility';
import type { PfFeatureWrite } from '../../../types/pf-digs';
import { blankToNull } from '../../../lib/pipeline-facility';
import { numOrNull } from '../../../lib/pf-digs';
import { PF_INPUT, PF_PRIMARY, PF_SECONDARY, PfField, PfFormError } from '../PfBits';
import { PfFormGrid, unitLabel } from './DigBits';

export interface FeatureInitial {
  feature_type?: string | null;
  description?: string | null;
  clock_position?: string | null;
  ut_min_wt?: number | null;
  pit_depth?: number | null;
  length?: number | null;
  width?: number | null;
  mpi_result?: string | null;
  assessment_result?: string | null;
  notes?: string | null;
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));

export function FeatureForm({
  initial,
  editing,
  catalog,
  wtUnit,
  anomalies,
  pending,
  error,
  submitLabel,
  onCancel,
  onSubmit,
  testId = 'pf-feature-form',
}: {
  initial?: FeatureInitial;
  /** An edit: cleared fields send null. A create sends only what was entered. */
  editing: boolean;
  catalog: PfCatalog | null;
  wtUnit: string | null | undefined;
  /** Undug anomalies of the site, offered on a create. */
  anomalies?: { id: string; label: string }[];
  pending: boolean;
  error: string | null;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (body: PfFeatureWrite) => void;
  testId?: string;
}) {
  const [f, setF] = useState({
    anomaly_id: '',
    feature_type: s(initial?.feature_type),
    description: s(initial?.description),
    clock_position: s(initial?.clock_position),
    ut_min_wt: s(initial?.ut_min_wt),
    pit_depth: s(initial?.pit_depth),
    length: s(initial?.length),
    width: s(initial?.width),
    mpi_result: s(initial?.mpi_result),
    assessment_result: s(initial?.assessment_result),
    notes: s(initial?.notes),
  });
  const [local, setLocal] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setF((x) => ({ ...x, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    setLocal(null);
    if (!f.anomaly_id && !f.feature_type) return setLocal('Choose the feature type.');
    const body: PfFeatureWrite = {};
    for (const k of ['ut_min_wt', 'pit_depth', 'length', 'width'] as const) {
      const n = numOrNull(f[k]);
      if (n !== null && !Number.isFinite(n)) return setLocal(`${k.replace(/_/g, ' ')} must be a number.`);
      if (n !== null || editing) body[k] = n;
    }
    const text = { description: f.description, clock_position: f.clock_position, notes: f.notes } as const;
    (Object.keys(text) as (keyof typeof text)[]).forEach((k) => {
      const v = blankToNull(text[k]);
      if (v !== null || editing) body[k] = v;
    });
    for (const k of ['mpi_result', 'assessment_result'] as const) {
      if (f[k] || editing) body[k] = f[k] || null;
    }
    if (f.feature_type) body.feature_type = f.feature_type;
    if (f.anomaly_id) body.anomaly_id = f.anomaly_id;
    onSubmit(body);
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={testId}>
      <PfFormError message={local ?? error} />
      <PfFormGrid>
        {!editing && anomalies && anomalies.length > 0 && (
          <PfField label="From the ILI list" hint="Or leave blank for a feature found in the ditch">
            <select className={PF_INPUT} aria-label="Anomaly" value={f.anomaly_id} onChange={set('anomaly_id')}>
              <option value="">Found in the ditch</option>
              {anomalies.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
          </PfField>
        )}
        <PfField label="Feature type">
          <select className={PF_INPUT} aria-label="Feature type" value={f.feature_type} onChange={set('feature_type')}>
            <option value="">{f.anomaly_id ? 'As on the ILI list' : 'Choose'}</option>
            {(catalog?.featureTypes ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Clock position" hint="1:00 to 12:59">
          <input className={PF_INPUT} aria-label="Clock position" placeholder="4:30" value={f.clock_position} onChange={set('clock_position')} />
        </PfField>
        <PfField label={unitLabel('UT minimum WT', wtUnit)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="UT minimum WT" value={f.ut_min_wt} onChange={set('ut_min_wt')} />
        </PfField>
        <PfField label={unitLabel('Pit depth', wtUnit)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Pit depth" value={f.pit_depth} onChange={set('pit_depth')} />
        </PfField>
        <PfField label={unitLabel('Length', wtUnit)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Feature length" value={f.length} onChange={set('length')} />
        </PfField>
        <PfField label={unitLabel('Width', wtUnit)}>
          <input className={PF_INPUT} inputMode="decimal" aria-label="Feature width" value={f.width} onChange={set('width')} />
        </PfField>
        <PfField label="MPI">
          <select className={PF_INPUT} aria-label="MPI result" value={f.mpi_result} onChange={set('mpi_result')}>
            <option value="">Not recorded</option>
            {(catalog?.mpiResults ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Assessment">
          <select className={PF_INPUT} aria-label="Assessment result" value={f.assessment_result} onChange={set('assessment_result')}>
            <option value="">Not assessed yet</option>
            {(catalog?.assessmentResults ?? []).map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </PfField>
        <PfField label="Description">
          <input className={PF_INPUT} aria-label="Feature description" value={f.description} onChange={set('description')} />
        </PfField>
      </PfFormGrid>
      <PfField label="Notes">
        <textarea className={`${PF_INPUT} h-16 py-2`} aria-label="Feature notes" value={f.notes} onChange={set('notes')} />
      </PfField>
      <div className="flex justify-end gap-2">
        <button type="button" className={PF_SECONDARY} onClick={onCancel} disabled={pending}>
          Cancel
        </button>
        <button type="submit" className={PF_PRIMARY} disabled={pending}>
          {pending ? 'Saving…' : submitLabel}
        </button>
      </div>
    </form>
  );
}
